import dayjs from "dayjs";
import type { UsagePeriodResponse } from "@Light/services/lightTypes";

import { Bar } from "react-chartjs-2";
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from "chart.js";
import { useScaffold } from "@Light/scaffold";
import { useHover } from "@Light/utils/hover";

Chart.register(CategoryScale, LinearScale, BarElement, Tooltip);

export type UsageChartProps = {
  periodUsage: UsagePeriodResponse[];
};

export const UsageChart: React.FC<UsageChartProps> = ({
  periodUsage,
}: UsageChartProps) => {
  const [ref, isHovered] = useHover<HTMLDivElement>();

  const scaffold = useScaffold();
  const backgroundColor = isHovered
    ? scaffold.page.colors.chart.unselected
    : scaffold.page.colors.chart.secondary;
  // Allocate a new array since reverse modifies the original array, and
  // that's a big no-no in React.
  const reverseReads = [...periodUsage];
  reverseReads.reverse();

  const labels = reverseReads.map((read) =>
    dayjs(read.period_end).format("MMM"),
  );
  const chartData = {
    labels,
    datasets: [
      {
        id: 1,
        label: "",
        data: reverseReads.map((read) => Math.round(Number(read.total_kwh))),
        backgroundColor,
        hoverBackgroundColor: scaffold.page.colors.chart.secondary,
        borderRadius: 3,
      },
    ],
  };
  const title = (items: { dataIndex: number }[]) => {
    const { dataIndex } = items[0];
    const read = reverseReads[dataIndex];
    return dayjs(read?.period_end).format("MMM D 'YY");
  };
  const label = (context: { parsed: { y: number | null } }) => {
    return `${context.parsed.y} kWh`;
  };
  return (
    <div ref={ref} className="w-full aspect-[2/1]">
      <Bar
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: true,
          interaction: {
            intersect: false,
            mode: "index",
          },
          scales: {
            y: {
              ticks: { display: false },
              border: { display: false },
            },
            x: {
              grid: { display: false },
              border: { display: false },
              ticks: {
                maxRotation: 0,
                minRotation: 0,
              },
            },
          },
          plugins: {
            tooltip: {
              enabled: true,
              displayColors: false,
              position: "nearest",
              callbacks: {
                title,
                label,
              },
            },
          },
        }}
      />
    </div>
  );
};
